/* ==========================================================================
   Onera — Cart core

   Shared fetch, events, and count sync for the ONERA AJAX cart system.
   Loaded before onera.js and cart drawer/page scripts. Vanilla only.
   ========================================================================== */
(function () {
  'use strict';

  var i18n = window.oneraCartI18n || {};
  var defaults = {
    addError: 'Could not add to your bag. Please try again.',
    changeError: 'Could not update your selection. Please try again.'
  };

  var changeSeq = 0;
  var sectionSeq = {};

  var dispatch = function (type, detail) {
    document.dispatchEvent(
      new CustomEvent(type, {
        detail: detail || {}
      })
    );
  };

  var fetchCart = function () {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(
      function (response) {
        if (!response.ok) throw new Error('Cart fetch failed');
        return response.json();
      }
    );
  };

  var fetchSection = function (sectionId) {
    return fetch('/?sections=' + encodeURIComponent(sectionId), {
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Section fetch failed');
        return response.json();
      })
      .then(function (data) {
        return data[sectionId] || '';
      });
  };

  var parseSection = function (html, selector) {
    if (!html) return null;
    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');
    return doc.querySelector(selector);
  };

  var updateCount = function (cart) {
    if (!cart || typeof cart.item_count !== 'number') return;
    if (typeof window.oneraUpdateCartCount === 'function') {
      window.oneraUpdateCartCount(cart.item_count);
    }
  };

  var getErrorMessage = function (error, type) {
    if (error && error.description) return error.description;
    if (error && error.message && error.message !== 'Cart add failed') {
      return error.message;
    }
    if (type === 'change') {
      return i18n.changeError || defaults.changeError;
    }
    return i18n.addError || defaults.addError;
  };

  var showError = function (host, message) {
    if (!host || !message) return;
    var el = host.querySelector('[data-onera-cart-error]');
    if (!el) {
      el = document.createElement('p');
      el.setAttribute('data-onera-cart-error', '');
      el.setAttribute('aria-live', 'polite');
      el.className = 'onera-cart__error';
      host.appendChild(el);
    }
    el.textContent = message;
    el.hidden = false;
    window.setTimeout(function () {
      if (el.textContent === message) {
        el.hidden = true;
        el.textContent = '';
      }
    }, 6000);
  };

  var clearError = function (host) {
    if (!host) return;
    var el = host.querySelector('[data-onera-cart-error]');
    if (el) {
      el.hidden = true;
      el.textContent = '';
    }
  };

  var normalizeAddResult = function (result) {
    if (result && result.items) return result.items;
    if (Array.isArray(result)) return result;
    return [result];
  };

  var addItems = function (items, source, meta) {
    var payloadItems = items;
    if (window.oneraCartPromotions && typeof window.oneraCartPromotions.enrichItems === 'function') {
      payloadItems = window.oneraCartPromotions.enrichItems(items, meta);
    }

    dispatch('onera:cart:adding', { items: payloadItems, source: source, meta: meta });

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: payloadItems })
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (err) {
            throw err;
          });
        }
        return response.json();
      })
      .then(function (result) {
        var itemsOut = normalizeAddResult(result);
        return fetchCart().then(function (cart) {
          dispatch('onera:cart:added', {
            cart: cart,
            items: itemsOut,
            item: itemsOut[0] || result,
            source: source,
            meta: meta,
            productId: meta && meta.productId,
            matchProductId: meta && meta.matchProductId
          });
          updateCount(cart);
          dispatch('onera:cart:updated', { cart: cart, source: source });
          return { cart: cart, items: itemsOut, result: result };
        });
      })
      .catch(function (error) {
        dispatch('onera:cart:add-error', {
          error: error,
          items: items,
          source: source,
          message: getErrorMessage(error, 'add')
        });
        throw error;
      });
  };

  var changeLine = function (key, quantity, source) {
    var seq = ++changeSeq;
    dispatch('onera:cart:changing', {
      key: key,
      quantity: quantity,
      source: source
    });

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (err) {
            throw err;
          });
        }
        return response.json();
      })
      .then(function (cart) {
        if (seq !== changeSeq) return cart;
        dispatch('onera:cart:changed', {
          cart: cart,
          key: key,
          quantity: quantity,
          source: source
        });
        updateCount(cart);
        return cart;
      })
      .catch(function (error) {
        if (seq === changeSeq) {
          dispatch('onera:cart:change-error', {
            error: error,
            key: key,
            quantity: quantity,
            source: source,
            message: getErrorMessage(error, 'change')
          });
        }
        throw error;
      });
  };

  var refreshSection = function (sectionId, source) {
    if (!sectionSeq[sectionId]) sectionSeq[sectionId] = 0;
    var seq = ++sectionSeq[sectionId];

    return fetchSection(sectionId).then(function (html) {
      if (seq !== sectionSeq[sectionId]) return null;
      return html;
    });
  };

  var openDrawer = function () {
    if (typeof window.oneraOpenCartDrawer === 'function') {
      window.oneraOpenCartDrawer();
      return;
    }
    dispatch('onera:cart:open');
  };

  window.oneraCartCore = {
    fetchCart: fetchCart,
    fetchSection: fetchSection,
    parseSection: parseSection,
    dispatch: dispatch,
    updateCount: updateCount,
    addItems: addItems,
    changeLine: changeLine,
    refreshSection: refreshSection,
    showError: showError,
    clearError: clearError,
    getErrorMessage: getErrorMessage,
    openDrawer: openDrawer
  };
})();
